/**
 * Runtime configuration, validated at module load.
 *
 * Why a single validated config object instead of reading import.meta.env
 * everywhere: catches missing or malformed env vars at boot, not when the
 * first API request fires. Mirrors the boot-time validation pattern we use
 * on the backend.
 */

import { z } from 'zod'

const schema = z.object({
  API_BASE_URL: z.string().min(1),
  ENV: z.enum(['development', 'qa', 'production']).default('development'),
  SENTRY_DSN: z.string().optional(),
  APP_VERSION: z.string().default('dev'),
})

function read(): z.infer<typeof schema> {
  // Vite injects these at build time. Dev defaults make `yarn dev` work
  // with no .env file; prod builds MUST set every required var via CI.
  const env = import.meta.env
  return schema.parse({
    API_BASE_URL: env.VITE_API_BASE_URL ?? '/api',
    ENV: env.VITE_ENV ?? env.MODE,
    SENTRY_DSN: env.VITE_SENTRY_DSN,
    APP_VERSION: env.VITE_APP_VERSION ?? 'dev',
  })
}

export const config = read()

export const isProd = config.ENV === 'production'
export const isDev = config.ENV === 'development'
